import * as React from "react";
import { setDropData } from "common/drop-controller";
import { massageCloudURL } from "common/components/portable-renderers";

export type DropData =
    | {
          kind: "file";
          file: File;
      }
    | {
          kind: "uri";
          uri: string;
      };

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    readonly onDropImage?: (imgHandle: string, data: DropData) => void;
    readonly onDragStart?: () => void;
    readonly onDragEnd?: () => void;
    readonly allowDrop: boolean;
    readonly innerRef?: React.RefObject<HTMLDivElement>;
}

const DroppableDiv: React.FunctionComponent<Props> = p => {
    const { onDropImage, onDragStart, onDragEnd, allowDrop, onDragOver, onDragLeave, onDrop, ...rest } = p;

    const [hovered, setHovered] = React.useState(false);

    let style = React.useMemo(() => {
        let r: React.CSSProperties = {
            transition: "transform 0.2s",
        };

        if (allowDrop && hovered) {
            r = {
                ...r,
                transform: "scale(1.1)",
                filter: "brightness(60%)",
            };
        }

        return r;
    }, [allowDrop, hovered]);

    const onDragOverCallback = React.useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (!allowDrop) return;
            event.preventDefault();
            event.stopPropagation();
            onDragStart?.call(undefined);
            onDragOver?.call(undefined, event);
            setHovered(true);
        },
        [allowDrop, onDragStart, onDragOver]
    );

    const onDragLeaveCallback = React.useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (!allowDrop) return;
            event.preventDefault();
            onDragEnd?.call(undefined);
            onDragLeave?.call(undefined, event);
            setHovered(false);
        },
        [allowDrop, onDragEnd, onDragLeave]
    );

    const onDropCallback = React.useCallback(
        (ev: React.DragEvent<HTMLDivElement>) => {
            if (!allowDrop) return;
            ev.preventDefault();
            ev.stopPropagation();
            onDrop?.call(undefined, ev);
            onDragEnd?.call(undefined);

            function onFileDropped(file: File): void {
                const data = { kind: "file" as const, file };
                onDropImage?.call(undefined, setDropData(data), data);
            }

            function onURIDropped(uri: string): void {
                const data = { kind: "uri" as const, uri: massageCloudURL(uri) };
                onDropImage?.call(undefined, setDropData(data), data);
            }

            if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to access the file(s)
                for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                    const item = ev.dataTransfer.items[i];
                    if (item.kind === "file") {
                        const file = item.getAsFile();
                        if (file !== null) {
                            onFileDropped(file);
                            // break for now since we dont know what to do with multiple
                            break;
                        }
                    } else if (item.kind === "string" && item.type === "text/uri-list") {
                        item.getAsString(uri => onURIDropped(uri));
                        // break for now since we dont know what to do with multiple
                        break;
                    }
                }
            } else {
                // Use DataTransfer interface to access the file(s)
                for (let i = 0; i < ev.dataTransfer.files.length; i++) {
                    const file = ev.dataTransfer.files[i];
                    onFileDropped(file);
                    // break for now since we dont know what to do with multiple
                    break;
                }
            }

            setHovered(false);
        },
        [onDrop, allowDrop, onDragEnd, onDropImage]
    );

    style = {
        ...style,
        ...p.style,
    };

    return (
        <div
            {...rest}
            style={style}
            onDragOver={onDragOverCallback}
            onDragLeave={onDragLeaveCallback}
            onDrop={onDropCallback}>
            {p.children}
        </div>
    );
};

export default DroppableDiv;
